import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    eventName: String,
    methodName: String,

    transactionId: String,
    currencyIso: String,
    totalAmount: String,
    taxAmount: String,
    itemPrice: String,
    itemName: String,
    itemId: String,
    itemCategory: String,
    hash: String,
    index: { type: Number, default: 1 },
    quantity: { type: Number, default: 1 },
    dataValue: Object
  }
  
  // Add static method for pushing data layer events from other controllers
  static pushToDataLayer(event_name, itemData, ecommerceData = {}) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: event_name,
      ecommerce: {
        ...ecommerceData,
        items: [{
          item_name: "custom_item_name",
          item_id: "custom_item_id",
          item_category: "custom_item_category",
          // index: 1,
          quantity: 1,
          id: "000",
          ...itemData
        }]
        
      }
    });
  }

  initialize() {
    window.dataLayer = window.dataLayer || [];
  }

  connect() {
    // Code used to call specific event on page load
    // (do not mistake with event call onclick or onsubmit)
    if (this.hasEventNameValue) {
      const isDirectMethodCall = this.eventNameValue.startsWith('->') ? true : false;
      if (isDirectMethodCall) {
        this.eventNameValue = this.eventNameValue.replace('->', '');
        this[this.eventNameValue]();
      } else {
        if (this.hasHashValue) {
          this.checkHashAndFireEvent();
          window.addEventListener('hashchange', this.checkHashAndFireEvent.bind(this));
        } else {
          this.pushEvent();
        }
      }
    }
  }

  checkHashAndFireEvent() {
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash === this.hashValue) {
      this.pushEvent();
    }
  }

  disconnect() {
    if (this.hasHashValue) {
      window.removeEventListener('hashchange', this.checkHashAndFireEvent.bind(this));
    }
  }

  addToCart() {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        items: [{
          item_name: this.itemNameValue,
          item_id: this.itemIdValue,
          item_category: this.itemCategoryValue,
          // index: this.indexValue,
          quantity: this.quantityValue,
          id: this.itemIdValue
        }],
      }
    });
  }

  nextPaymentInfo(event) {
    if (event.target.checked) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_payment_info",
        ecommerce: {
          items: [{
            item_name: this.itemNameValue,
            item_id: this.itemIdValue,
            item_category: this.itemCategoryValue,
            // index: this.indexValue,
            quantity: this.quantityValue,
            id: this.itemIdValue
          }]
        }
      });
    }
  }
  
  nextBillingInfo() {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_billing_info",
      ecommerce: {
        items: [{
          item_name: this.itemNameValue,
          item_id: this.itemIdValue,
          item_category: this.itemCategoryValue,
          // index: this.indexValue,
          quantity: this.quantityValue,
          id: this.itemIdValue
        }]
      }
    });
  }

  nextBillingDetails() {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "add_shipping_info",
      ecommerce: {
        items: [{
          item_name: this.itemNameValue,
          item_id: this.itemIdValue,
          item_category: this.itemCategoryValue,
          // index: this.indexValue,
          quantity: this.quantityValue,
          id: this.itemIdValue
        }]
      }
    });
  }

  purchase() {
    if (this.allowSingleEvaluation(`purchase_${this.transactionIdValue}`)) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "purchase",
        ecommerce: {
          transactionId: this.transactionIdValue,
          value: this.totalAmountValue,
          tax: this.taxAmountValue,
          currency: this.currencyIsoValue,
          items: [{
            item_name: this.itemNameValue,
            item_id: this.itemIdValue,
            item_category: this.itemCategoryValue,
            quantity: this.quantityValue,
            id: this.itemIdValue,
            price: this.itemPriceValue
          }]
        }
      });
    }
  }

  searchSubmitHomepage(event) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "search",
      search_type: "homepage_search"
    });
  }

  searchSubmit(event) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "search_all",
      search_type: "results_page_search"
    });
  }

  pushEvent() {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: this.eventNameValue,
      ecommerce: {
        items: [{
          item_name: this.itemNameValue,
          item_id: this.itemIdValue,
          item_category: this.itemCategoryValue,
          // index: this.indexValue,
          quantity: this.quantityValue,
          id: this.itemIdValue,
          ...(this.hasDataValueValue ? this.dataValue : {})
        }],
      },
    });
  }

  bookingTogglePocketWifi(event) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: "checkout_toggle_pocket_wifi",
      ecommerce: {
        items: [{
          item_name: this.itemNameValue,
          item_id: this.itemIdValue,
          item_category: this.itemCategoryValue,
          item_status: event.target.checked ? "added_pocket_wifi" : "removed_pocket_wifi",
          // index: this.indexValue,
          quantity: event.target.checked ? 1 : 0,
          id: this.itemIdValue,
        }]
      }
    });
  }
  // Method to allow single evaluation
  allowSingleEvaluation(key) {
    const cookieName = `ase_${key}`.toLowerCase();
    
    // Check if the cookie exists
    // If it exists, it means the event has already been executed
    const cookies = document.cookie.split(';');
    for(let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf(cookieName + '=') === 0) {
        // Cookie exists, event already executed
        return false;
      }
    }
    
    // Cookie doesn't exist
    // Set cookie and allow evaluation
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days
    document.cookie = `${cookieName}=true;expires=${expiryDate.toUTCString()};path=/`;
    return true;
  }

}
